"use client";

import { useState } from "react";
import Link from "@/lib/localized-link";
import { useAuthHref } from "@/lib/use-return-to";
import { useAuth } from "@/lib/auth-context";
import { useT, useLocale } from "@/lib/locale-context";
import type { RateLimitError } from "@/lib/api";
import { Lock, Mail, Loader2, CheckCircle2 } from "lucide-react";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

interface Props {
  error: RateLimitError;
}

/**
 * What the gated pages show when the backend refuses a run. Since the
 * analysis limits went (owner, 2026-10-18) there are three cases only:
 * an unverified email/password account, a device that signed in before
 * (sign back in), and an anonymous device whose free analyses are used
 * up (sign up, an account has no limit). A signed-in account is never
 * refused for volume, so the old wait countdown, the "buy tokens" and
 * the zero-balance trial pitch are gone.
 */
export default function RateLimitBanner({ error }: Props) {
  const { user } = useAuth();
  const t = useT();
  // Auth CTAs return the visitor to the page where they hit the wall.
  const signinHref = useAuthHref("login");
  const signupHref = useAuthHref("register");

  // Unverified path: a verify-email prompt + resend button. Renders
  // before the regular paths so it owns the entire banner when active.
  if (error.code === "email_not_verified") {
    return <UnverifiedBanner />;
  }

  // Device-had-account path: this browser was signed in at some point and
  // is anonymous now, so the anonymous free allowance is withheld. Ask
  // them to sign back IN (they already have an account) rather than sign up.
  if (error.code === "account_required") {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
        <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-amber-100 border border-amber-200 flex items-center justify-center">
          <Lock className="w-6 h-6 text-amber-700" />
        </div>
        <h3 className="text-lg font-bold text-amber-900 mb-1">
          {t("rate.account_required.title")}
        </h3>
        <p className="text-sm text-amber-800 mb-4 leading-relaxed">
          {t("rate.account_required.body")}
          {error.email_hint && (
            <span className="mt-1 block font-semibold">{t("rate.account_required.hint").replace("{email}", error.email_hint)}</span>
          )}
        </p>
        <Link
          href={signinHref}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors"
        >
          {t("auth.signin")}
        </Link>
      </div>
    );
  }

  // Anonymous wall: the device's free analyses are used up. The CTA is
  // a Link to /auth/register so both sign-up options are visible.
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
      {/* Amber squircle - matches the empty-state / intent-pill pattern
          used across the rest of the app so the banner doesn't stand
          out as a different shape language. */}
      <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-amber-100 border border-amber-200 flex items-center justify-center">
        <Lock className="w-6 h-6 text-amber-700" />
      </div>
      {/* Amber-* foreground (not gray-*) keeps text readable on the
          dark-mode surface - gray-900 / 700 invert and disappear
          against the amber-50 backdrop under the .dark token map. */}
      <h3 className="text-lg font-bold text-amber-900 mb-1">
        {t("rate.freeused")}
      </h3>
      <p className="text-sm text-amber-800 mb-4 leading-relaxed">
        {t("rate.signinhint")}
      </p>
      {!user && (
        <Link
          href={signupHref}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors"
        >
          {t("auth.signup")}
        </Link>
      )}
    </div>
  );
}

// Module scope: declared inside RateLimitBanner it was a new component
// type on every render, so each parent render remounted it (a sent
// resend flipped back).
function UnverifiedBanner() {
  const t = useT();
  const { locale } = useLocale();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const handleResend = async () => {
    setSending(true);
    setErr("");
    try {
      const res = await fetch(`${API_BASE}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ locale }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErr(data.detail || t("auth.verify.resend.failed"));
      } else {
        setSent(true);
      }
    } catch {
      setErr(t("auth.verify.network"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6 text-center">
      {/* Title + body use amber-* (not gray-*) so they auto-swap
          to legible LIGHT foreground in dark mode. gray-900 / 700
          invert to DARK colours under the .dark token map and went
          near-invisible against the dark amber surface. Same fix
          as MortgageTransferBanner. */}
      <h3 className="text-lg font-bold text-amber-900 mb-1">
        {t("rate.unverified.title")}
      </h3>
      <p className="text-sm text-amber-800 mb-5 leading-relaxed max-w-md mx-auto">
        {t("rate.unverified.body")}
      </p>
      {sent ? (
        <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
          <CheckCircle2 className="w-4 h-4" />
          {t("auth.verify.resend.sent")}
        </div>
      ) : (
        <button
          type="button"
          onClick={handleResend}
          disabled={sending}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors"
        >
          {sending && <Loader2 className="w-4 h-4 animate-spin" />}
          {!sending && <Mail className="w-4 h-4" />}
          {t("auth.verify.resend")}
        </button>
      )}
      {err && (
        <p className="mt-3 text-xs text-rose-700">{err}</p>
      )}
    </div>
  );
}
